import { CommandRing } from '../state/commandRing';
import { COMMAND_BUFFER_SIZE, TELEMETRY_BUFFER_SIZE } from '../state/layout';
import { TelemetryBuffer, type TelemetrySnapshot } from '../state/telemetryBuffer';
import type {
  ScenarioSource,
  SimWorkerRequest,
  SimWorkerResponse,
  SimWorkerStat,
} from './simWorker';

export interface WorkerMessageEvent {
  data: SimWorkerResponse;
}

export interface WorkerLike {
  postMessage(message: SimWorkerRequest): void;
  addEventListener(type: 'message', listener: (event: WorkerMessageEvent) => void): void;
  terminate(): void;
}

export type SimWorkerFactory = () => WorkerLike;
export type FatalListener = (reason: string) => void;
export type StatListener = (stat: SimWorkerStat) => void;

const defaultWorkerFactory: SimWorkerFactory = () =>
  new Worker(new URL('./simWorker.ts', import.meta.url), { type: 'module' }) as WorkerLike;

const requireSharedArrayBuffer = (): typeof SharedArrayBuffer => {
  const SharedBuffer = globalThis.SharedArrayBuffer;
  const explicitlyNotIsolated =
    typeof globalThis.crossOriginIsolated === 'boolean' && !globalThis.crossOriginIsolated;
  if (typeof SharedBuffer === 'undefined' || explicitlyNotIsolated) {
    throw new Error(
      'Simulation worker needs SharedArrayBuffer and cross-origin isolation; serve with COOP/COEP headers.',
    );
  }
  return SharedBuffer;
};

/** Main-thread facade for the shared buffers and worker control protocol. */
export class SimWorkerClient {
  readonly commandRing: CommandRing;

  private readonly worker: WorkerLike;
  private readonly telemetry: TelemetryBuffer;
  private readonly fatalListeners = new Set<FatalListener>();
  private readonly statListeners = new Set<StatListener>();
  private lastFatal: string | null = null;

  constructor(scenario: ScenarioSource, workerFactory: SimWorkerFactory = defaultWorkerFactory) {
    const SharedBuffer = requireSharedArrayBuffer();
    const telemetryBuffer = new SharedBuffer(TELEMETRY_BUFFER_SIZE);
    const commandBuffer = new SharedBuffer(COMMAND_BUFFER_SIZE);
    this.telemetry = new TelemetryBuffer(telemetryBuffer);
    this.commandRing = new CommandRing(commandBuffer);
    this.worker = workerFactory();
    this.worker.addEventListener('message', (event) => this.onMessage(event.data));
    this.worker.postMessage({
      type: 'init',
      telemetryBuffer,
      commandBuffer,
      scenario,
    });
  }

  loadScenario(scenario: ScenarioSource): void {
    this.lastFatal = null;
    this.worker.postMessage({ type: 'loadScenario', scenario });
  }

  reset(): void {
    this.worker.postMessage({ type: 'reset' });
  }

  setTimeScale(timeScale: number): void {
    this.worker.postMessage({ type: 'setTimeScale', timeScale });
  }

  pause(): void {
    this.worker.postMessage({ type: 'pause' });
  }

  resume(): void {
    this.worker.postMessage({ type: 'resume' });
  }

  stepSeconds(seconds: number): void {
    if (!Number.isFinite(seconds) || seconds < 0) {
      throw new RangeError('seconds must be a finite non-negative number');
    }
    this.worker.postMessage({ type: 'stepSeconds', seconds });
  }

  onFatal(listener: FatalListener): () => void {
    this.fatalListeners.add(listener);
    if (this.lastFatal !== null) listener(this.lastFatal);
    return () => this.fatalListeners.delete(listener);
  }

  onStat(listener: StatListener): () => void {
    this.statListeners.add(listener);
    return () => this.statListeners.delete(listener);
  }

  readTelemetry(): TelemetrySnapshot | null {
    return this.telemetry.read();
  }

  dispose(): void {
    this.worker.terminate();
    this.fatalListeners.clear();
    this.statListeners.clear();
  }

  private onMessage(message: SimWorkerResponse): void {
    if (message.type === 'fatal') {
      this.lastFatal = message.reason;
      this.fatalListeners.forEach((listener) => listener(message.reason));
    } else if (message.type === 'stat') {
      const stat: SimWorkerStat = {
        fps: message.fps,
        realtimeFactor: message.realtimeFactor,
        slow: message.slow,
      };
      this.statListeners.forEach((listener) => listener(stat));
    }
  }
}
